import { Button, Modal, type ModalProps } from 'flowbite-react';
import { AlertTriangleIcon, CircleHelpIcon, LoaderCircleIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

export interface ConfirmationModalProps extends ModalProps {
    message: string|React.ReactNode;
    isDanger?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => Promise<boolean>;
    onCancel?: () => void;
}

export const ConfirmationModal = ({
    show,
    message,
    isDanger,
    confirmLabel = 'Potvrdit',
    cancelLabel = 'Zrušit',
    onCancel,
    onConfirm,
    onClose,
    size = 'md',
    ...props
}: ConfirmationModalProps) => {
    const [ isLoading, setIsLoading ] = useState(false);

    const handleConfirm = useCallback(() => {
        setIsLoading(true);

        // Give React a frame to commit the spinner before potentially heavy
        // sync work in onConfirm starts — otherwise the loader never paints.
        requestAnimationFrame(() => {
            const start = Date.now();

            onConfirm().then(async (result) => {
                // Hold the loader visible for a minimum so the user perceives it
                // even when the work happens to be fast.
                const MIN_VISIBLE_MS = 600;
                const elapsed = Date.now() - start;
                if (elapsed < MIN_VISIBLE_MS) {
                    await new Promise<void>((resolve) => setTimeout(resolve, MIN_VISIBLE_MS - elapsed));
                }
                return result;
            }).then((result) => {
                if (result) {
                    onClose?.();
                }
            }).finally(() => {
                setIsLoading(false);
            });
        });
    }, [ onClose, onConfirm ]);

    const handleCancel = useCallback(() => {
        onCancel?.();
        onClose?.();
    }, [ onClose ]);

    return (
        <Modal show={show} size={size} onClose={onClose} popup {...props}>
            <Modal.Header />
            <Modal.Body>
                <div className="text-center">
                    <div className='flex flex-col items-center gap-4'>
                        {isDanger && (
                            <AlertTriangleIcon size={42} className='text-red-600' />
                        )}
                        {!isDanger && (
                            <CircleHelpIcon size={42} className='text-gray-400' />
                        )}
                        <h3 className="mb-5 text-md font-medium text-gray-800 dark:text-gray-400">{message}</h3>
                    </div>
                    <div className="flex justify-center gap-4">
                        <Button
                            color={isDanger ? 'failure' : 'primary'}
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading && (
                                <LoaderCircleIcon size={16} className='animate-spin mr-2 flex-shrink-0' />
                            )}
                            {confirmLabel}
                        </Button>
                        <Button
                            color="gray"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >{cancelLabel}</Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};
